import service from "./service";

const assistants = async (payload) => {
  return service.post("/assistants", payload).then((response) => {
    return response;
  });
};

const getAssistants = async () => {
  return service.get("/assistants").then((response) => {
    return response;
  });
};

const getAssistantsId = async (assistId) => {
  return service.get(`/assistants/${assistId}`).then((response) => {
    return response;
  });
};

// The widget's public config: branding, theme and suggested prompts. Unlike
// the other endpoints this one needs no session.
const getAssistantConfig = async (assistId) => {
  return service.get(`/assistants/${assistId}/config`).then((response) => {
    return response;
  });
};

// Re-reads the customer's site. Answers 201 with the record already flipped
// to "crawling"; progress is then polled from GET /assistants/:id.
const recrawlAssistant = async (assistId) => {
  return service.post(`/assistants/${assistId}/recrawl`, {}).then((response) => {
    return response;
  });
};

// The live install checks: script detected, widget loaded, domain verified,
// SSL, and the crawl's own state.
const getInstallationStatus = async (assistId) => {
  return service
    .get(`/assistants/${assistId}/installation-status`)
    .then((response) => {
      return response;
    });
};

// Dashboard metrics for a date window; both dates are YYYY-MM-DD.
const getAssistantOverview = async (assistId, { from, to }) => {
  return service
    .get(`/assistants/${assistId}/overview`, { params: { from, to } })
    .then((response) => {
      return response;
    });
};

const getConversations = async (assistId, params = {}) => {
  return service
    .get(`/assistants/${assistId}/conversations`, { params })
    .then((response) => {
      return response;
    });
};

// Messages live on their own endpoint, not inside the conversation record.
const getConversationMessages = async (assistId, conversationId) => {
  return service
    .get(`/assistants/${assistId}/conversations/${conversationId}/messages`)
    .then((response) => {
      return response;
    });
};

// The Activity tab: started, status changes, lead captured, tag edits.
const getConversationActivity = async (assistId, conversationId) => {
  return service
    .get(`/assistants/${assistId}/conversations/${conversationId}/activity`)
    .then((response) => {
      return response;
    });
};

// Daily conversation counts for the analytics chart. Both dates are
// YYYY-MM-DD; the API defaults to the last seven days when they are omitted.
const getConversationTimeseries = async (assistId, params = {}) => {
  return service
    .get(`/assistants/${assistId}/conversations/timeseries`, { params })
    .then((response) => {
      return response;
    });
};

// Top topics with share percentages, from per-turn topic labelling.
const getConversationTopics = async (assistId, params = {}) => {
  return service
    .get(`/assistants/${assistId}/conversations/topics`, { params })
    .then((response) => {
      return response;
    });
};

const getConversationById = async (assistId, conversationId) => {
  return service
    .get(`/assistants/${assistId}/conversations/${conversationId}`)
    .then((response) => {
      return response;
    });
};

// A returning visitor's stored conversation, used to restore the thread
// before the socket connects. Public - no session needed.
const getWidgetState = async (assistId, visitorId) => {
  return service
    .get(`/assistants/${assistId}/widget-state`, { params: { visitorId } })
    .then((response) => {
      return response;
    });
};

// Rolls the facts learned during training into the assistant's memory summary.
const summarizeMemory = async (assistId) => {
  return service
    .post(`/assistants/${assistId}/memory/summarize`)
    .then((response) => {
      return response;
    });
};

// PATCH, not PUT — the endpoint takes a partial body.
const UpdateAssistantsId = async (assistId, payload) => {
  return service.patch(`/assistants/${assistId}`, payload).then((response) => {
    return response;
  });
};

// DELETE /assistants/:id - moves it to the trash. It stops counting towards
// the plan's allowance, which is how an account gets small enough to take a
// lower plan. Works while the account is paused.
const deleteAssistant = async (assistId) => {
  return service.deleteApi(`/assistants/${assistId}`).then((response) => {
    return response;
  });
};

// GET /assistants/:id/knowledge-status - what the crawl is doing right now:
// the stage, the pages read against the limit, and, once it has finished, the
// content check and the business summary it built.
const getKnowledgeStatus = async (assistId) => {
  return service.get(`/assistants/${assistId}/knowledge-status`).then((response) => {
    return response;
  });
};

const assistantsService = {
  assistants,
  deleteAssistant,
  getKnowledgeStatus,
  getAssistants,
  getAssistantsId,
  getAssistantConfig,
  getInstallationStatus,
  recrawlAssistant,
  getAssistantOverview,
  getConversations,
  getConversationTimeseries,
  getConversationTopics,
  getConversationById,
  getConversationMessages,
  getConversationActivity,
  summarizeMemory,
  getWidgetState,
  UpdateAssistantsId,
};

export default assistantsService;